import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ImageGallery } from 'shared/components/imageGallery/ImageGallery';
import { Timeline } from 'shared/components/Timeline/Timeline';
import { useConfig } from 'shared/config/ConfigContext';
import type { DeviceType } from 'shared/types';
import { CancellationRulesList } from '@/components/cancellationPolicy/CancellationRulesList';
import { FhhBreadcrumbs } from '@/components/fhhBreadcrumbs/FhhBreadcrumbs';
import { SafetyInfo } from '@/components/safetyInfo/SafetyInfo';
import type { FhhState, FhhUnleashToggles } from '@/domain';
import { fetchObjectRecommendations, parseNumGuests, populateObjectPriceInfo } from '@/ducks/object/objectActions';
import { useExperiment } from '@/hooks/experiments';
import { useAvailabilityPerDate } from '@/hooks/useAvailabilityPerDate';
import { useCurrentCancellationPolicy } from '@/hooks/useCurrentCancellationPolicy';
import { useHostBadges } from '@/hooks/useHostBadges';
import { useObject } from '@/hooks/useObject';
import { usePreview } from '@/hooks/usePreview';
import { publishBannerEvent } from '@/lib/helpers/banner';
import { trackAdView } from '@/lib/tracking/objectTracking';
import { type ObjectTrackingStatus, trackObjectPageView } from '@/lib/tracking/tracking';
import type { CancellationPolicy } from '@/models/cancellationPolicy';
import { LoginToSeeProfile } from '@/scenes/objectPage/components/loginToSeeProfile/LoginToSeeProfile';
import { Administration } from './components/administration/Administration';
import { AreaProfile } from './components/areaProfile/AreaProfile';
import { Description } from './components/description/Description';
import { DigitalContract } from './components/digitalContract/DigitalContract';
import { FavoriteButton } from './components/favoriteButton/FavoriteButton';
import { Footer } from './components/footer/Footer';
import { HouseRules } from './components/houseRules/HouseRules';
import { Info } from './components/info/Info';
import { InfoList } from './components/infoList/InfoList';
import { Links } from './components/links/Links';
import { LocationMap } from './components/locationMap/LocationMap';
import { Panel } from './components/panel/Panel';
import { Profile } from './components/profile/Profile';
import { Rating } from './components/rating/Rating';
import { RatingDesktop } from './components/rating/RatingDesktop';
import { RecommendationList } from './components/recommendationList/RecommendationList';
import { Status } from './components/status/Status';
import { Calendar } from './containers/calendar/Calendar';
import { FloatingPanel } from './containers/floatingPanel/FloatingPanel';

export interface ObjectPageProps {
    deviceType: DeviceType;
    loginId: number | null;
    unleash: FhhUnleashToggles;
    validDates: boolean;
}

type AdImage = {
    uri: string;
    width: number;
    height: number;
    description?: string;
};

const parseQueryParam = (val: string | string[]): string => {
    if (Array.isArray(val)) {
        return val.join(',');
    }

    return val;
};

export const ObjectPage = ({ deviceType, loginId, unleash, validDates }: ObjectPageProps) => {
    const dispatch = useDispatch();
    const router = useRouter();
    const experiment = useExperiment();
    const showTimelineComponent = experiment?.['travel-fhh-tjvr-object-page-drop-off-reduction'] === 'treatment';
    const { apiExternalUrl, contextPath } = useConfig();
    const objectData = useObject();
    const isPreview = usePreview();
    const hostBadges = useHostBadges();
    const profileIdentity = useSelector((state: FhhState) => state.profileData.identity);
    const norwegianAddress = objectData?.location?.countryCode === 'NO';
    const selectedDates = {
        from: validDates && router.query.fra ? new Date(parseQueryParam(router.query.fra)) : null,
        to: validDates && router.query.til ? new Date(parseQueryParam(router.query.til)) : null,
    };
    const selectedGuests = parseNumGuests(router.query.gjester, objectData.noOfBeds);
    const { recommendationData } = useSelector((state: FhhState) => state.objectData);
    const {
        adId,
        areaDescription,
        description,
        facilities,
        houseRules,
        heading,
        images,
        isPrivate,
        location,
        modified,
        moreInfo,
        poiNearBy,
        residenceDescription,
        roomsDescription,
        status,
        user,
    } = objectData;

    let currentCancellationPolicy: CancellationPolicy | undefined;
    if (isPrivate && norwegianAddress) {
        // biome-ignore lint/correctness/useHookAtTopLevel: Conditional hooks not allowed. Should be fixed.
        currentCancellationPolicy = useCurrentCancellationPolicy();
    }

    const [initialBannerEventPublished, setInitialBannerEventPublished] = useState(false);
    const isMobile = Boolean(deviceType.isProbablyMobile);
    const isLoggedIn = loginId != null;
    const ratingRef = useRef(null);
    const availabilityPerDate = useAvailabilityPerDate();
    const filters = useSelector((state: FhhState) => state.results.filters);
    const ciParam = router.query.ci;
    const currentImageIndex = ciParam ? Number.parseInt(Array.isArray(ciParam) ? ciParam[0] : ciParam, 10) - 1 : 0;

    const renderProfileAndCalendar = () => (
        <div className="space-y-16">
            {isPrivate && !isLoggedIn ? (
                <LoginToSeeProfile />
            ) : (
                <Profile
                    hostBadges={hostBadges}
                    isImport={objectData.isImport}
                    isPrivate={isPrivate}
                    organisation={objectData.organisation}
                    owner={objectData.owner}
                    profileIdentity={profileIdentity}
                />
            )}

            {!isMobile && (
                <div className="p-16 border rounded-8">
                    <Calendar
                        isLoggedIn={isLoggedIn}
                        selectedDates={selectedDates}
                        selectedNumGuests={selectedGuests}
                        unleash={unleash}
                        norwegianAddress={norwegianAddress}
                    />
                </div>
            )}
            {!isPreview && !isMobile && isPrivate && <RatingDesktop adId={adId} ratingRef={ratingRef} />}
            {!norwegianAddress && isPrivate && unleash?.removeSwiftcortFromObjectPage && (
                <Panel title="Trenger du en kontrakt?" info bleed>
                    <DigitalContract adId={adId} isPreview={isPreview} />
                </Panel>
            )}
            <Links links={moreInfo} />
        </div>
    );

    const convertImages = () =>
        images?.map((image): AdImage => ({ uri: image.src, width: image.width, height: image.height, description: image.description }));

    const ignoredFacilities: ReadonlyArray<string> = ['Røyking tillatt', 'Rullestolvennlig', 'Kjæledyr tillatt'];
    const facilitiesWithoutHouseRules = facilities ? facilities.filter((facility) => !ignoredFacilities.includes(facility)) : null;

    useEffect(() => {
        if (isPreview || !adId) {
            return;
        }
        const status: ObjectTrackingStatus = objectData?.status?.isActive
            ? 'published'
            : objectData?.status?.isPaused
              ? 'inactive'
              : 'draft';
        trackObjectPageView(adId, status, objectData?.isImport ? objectData.owner?.name : undefined);
        trackAdView({ adId });
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    }, [adId, isPreview, objectData?.isImport, objectData?.owner?.name]);

    useEffect(() => {
        if (isPreview || !adId || initialBannerEventPublished) {
            return;
        }

        publishBannerEvent();
        setInitialBannerEventPublished(true);
    }, [adId, initialBannerEventPublished, isPreview]);

    useEffect(() => {
        if (isPreview || !adId || !isPrivate) {
            return;
        }

        dispatch(populateObjectPriceInfo(apiExternalUrl, adId));
    }, [adId, isPreview, isPrivate, dispatch]);

    useEffect(() => {
        if (isPreview || !adId) {
            return;
        }

        dispatch(fetchObjectRecommendations(contextPath, adId));
    }, [adId, isPreview, dispatch]);

    return (
        <div className="ml-0 mb-24" data-testid="objectPage">
            <FhhBreadcrumbs className="mx-0 py-8" isPreview={isPreview} />
            <Administration adId={adId} isPreview={isPreview} user={user} />
            <ImageGallery
                itemId={adId}
                userId={loginId ?? undefined}
                initialImage={currentImageIndex}
                images={convertImages()}
                data-testid="adImageContainer"
                roundAllCorners={true}
                isMobile={isMobile}
                favoriteHeartButton={<FavoriteButton itemId={adId} variant="heart" isOpen={false} itemType="Ad" />}
            />
            <div className="grid md:grid-cols-3 gap-24 pb-32">
                <article className="md:col-span-2 space-y-40">
                    <div className="mx-0 space-y-32">
                        {status && <Status status={status} />}
                        <h1 data-testid="adHeading" className="break-words">
                            {heading}
                        </h1>
                        <Info
                            estateSize={objectData.estateSize}
                            noOfBedrooms={objectData.noOfBedrooms}
                            noOfBeds={objectData.noOfBeds}
                            noOfBathrooms={objectData.noOfBathrooms}
                            propertyType={objectData.propertyType}
                        />
                    </div>
                    {norwegianAddress && isPrivate && showTimelineComponent ? (
                        <Timeline
                            title="Slik fungerer en forespørsel på FINN"
                            steps={[
                                'Du sender en forespørsel til utleier',
                                'Utleier svarer deg senest innen 48 timer',
                                'Du velger selv om du vil bekrefte bestillingen',
                            ]}
                        />
                    ) : (
                        <SafetyInfo smallText={false} />
                    )}
                    {residenceDescription && (
                        <Panel title="Om boligen" data-testid="residence-title">
                            <Description description={residenceDescription} data-testid="residence-description" />
                        </Panel>
                    )}
                    {!residenceDescription && description && (
                        <Panel title="Beskrivelse" data-testid="description-title">
                            <Description description={description} data-testid="description" />
                        </Panel>
                    )}
                    {roomsDescription && (
                        <Panel title="Rom og sengeplasser">
                            <Description description={roomsDescription} data-testid="room-description" />
                        </Panel>
                    )}
                    <InfoList title="Fasiliteter" list={facilitiesWithoutHouseRules} split />
                    <hr />
                    {houseRules && (
                        <>
                            <Panel title="Husregler og tilgjengelighet" data-testid="houserules">
                                <HouseRules houseRules={houseRules} />
                            </Panel>
                            <hr />
                        </>
                    )}
                    {norwegianAddress && isPrivate ? (
                        <>
                            <Panel title="Regler for avbestilling" data-testid="cancellation">
                                <CancellationRulesList policy={currentCancellationPolicy} />
                            </Panel>
                            <hr />
                        </>
                    ) : null}
                    {areaDescription && (
                        <Panel title="Beliggenheten" data-testid="area-title">
                            <Description description={areaDescription} data-testid="area-description" />
                        </Panel>
                    )}
                    <InfoList title="I nærheten" list={poiNearBy} />
                    <LocationMap {...location} />
                    {isMobile && <div data-testid="objectPage-mobileProfileAndCalendar">{renderProfileAndCalendar()}</div>}
                    {norwegianAddress && isPrivate && (
                        <>
                            <hr />
                            <h2 className="h3 space-x-16">Nabolagsprofil</h2>
                            <AreaProfile adId={adId} />
                        </>
                    )}
                    {!isPreview && isPrivate && (
                        <>
                            <hr />
                            <Panel title="Vurderinger fra gjester" ref={ratingRef}>
                                <Rating adId={adId} isMobile={isMobile} />
                            </Panel>
                        </>
                    )}
                    <hr />
                    <Footer adId={adId} heading={heading} isPreview={isPreview} modified={modified} />
                </article>
                {!isMobile && <div data-testid="objectPage-profileAndCalendar">{renderProfileAndCalendar()}</div>}
            </div>

            {recommendationData?.items && recommendationData.items.length > 0 && (
                <Panel title="Andre feriehjem og hytter" data-testid="objectPage-recommendations">
                    <RecommendationList items={recommendationData.items} />
                </Panel>
            )}

            {isMobile && (
                <FloatingPanel
                    initialSelectedDates={selectedDates}
                    isLoggedIn={isLoggedIn}
                    unleash={unleash}
                    norwegianAddress={norwegianAddress}
                    objectData={objectData}
                    availabilityPerDate={availabilityPerDate}
                    isPreview={isPreview}
                    filters={filters}
                    initialNumGuests={selectedGuests}
                />
            )}
        </div>
    );
};
